import { useCurrentCast } from '@/modules/casts/compositions'
import useReplayObjects from './replayObjects'
import { PartialRequired } from '@/types'
import { RecordedSource } from '../classes'
import store from '@/store'
import { Stream } from '@/types/streams'
import { Result } from '@/modules/errorHandling/results'

// Source name defaults to 'Recording_'+id
const setRecordedSource = async (params: PartialRequired<RecordedSource, 'id'|'url'>): Promise<void> => {
  await store.dispatch.replayConfig.setRecordedSource({ id: params.id, url: params.url,
    name: params.name ?? '', inputStreamId: params.inputStreamId ?? null })
}

const useRecordedSources = () => {
  const { currentCastId } = useCurrentCast()
  const { recordedSources } = useReplayObjects(currentCastId)

  // Map a stream ID to a specific recorded source
  const setStreamForRecordedSource = async (params: { id: string, inputStreamId: string|null,
    name?: string|null }): Promise<void> =>
  {
    const recordedSource = recordedSources.value[params.id]
    const name = params.inputStreamId && params.name ? params.name : null
    if (!recordedSource) throw new Error (`Failed to set input on recorded source: ${params.id}. Source not found`)
    await store.dispatch.replayConfig.setRecordedSource({ id: params.id, url: recordedSource.url,
      name, inputStreamId: params.inputStreamId })
  }

  // Map a stream to any available recorded source
  const mapStreamToAvailableRecorder = async (inputStream: Stream):
    Promise<Result<string, 'invalid_params'|'resource_unavailable'>> =>
  {
    if (!inputStream.id) return Result.fail('invalid_params', 'Invalid input stream')
    const recordedSourceList = Object.values(recordedSources.value)
    const unmappedRecordedSource = recordedSourceList.filter((source) => !source.inputStreamId)
    if (unmappedRecordedSource.length === 0) return Result.fail('resource_unavailable', 'No available recording source')
    const mappedRecordedSource = recordedSourceList.find((source) => source.inputStreamId === inputStream.id)

    if (mappedRecordedSource) return Result.success(mappedRecordedSource.id)
    await setStreamForRecordedSource({ id: unmappedRecordedSource[0].id, inputStreamId: inputStream.id,
      name: inputStream.name })
    return Result.success(unmappedRecordedSource[0].id)
  }

  const getMappedRecordedSource = (inputStreamId: string): RecordedSource|undefined => {
    const recordedSourceList = Object.values(recordedSources.value)
    return recordedSourceList.find((recordedSource) => recordedSource.inputStreamId === inputStreamId)
  }

  return {
    getMappedRecordedSource,
    mapStreamToAvailableRecorder,
    setRecordedSource,
    setStreamForRecordedSource
  }
}

export default useRecordedSources
